/**
 * Historical USD/ARS (dolar bolsa) lookup with client-side cache.
 */
import { autoConversionRatesForDisplay } from "shared/money/currencyDefaults";
import { responseCache } from "shared/money/responseCache";
import { fetchHistoricalRates, type DollarQuote } from "features/market";

const BOLSA_HISTORY_ENDPOINT = "/market/dollar-rates/bolsa/history";
const REFERENCE_DOLLAR_TYPE = "bolsa" as const;

let memoryHistory: DollarQuote[] | null = null;
let memoryLoadPromise: Promise<DollarQuote[]> | null = null;

function normalizeDateKey(date: string | Date): string {
    if (date instanceof Date) {
        if (isNaN(date.getTime())) return "";
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    }
    const trimmed = String(date ?? "").trim();
    if (!trimmed) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
    const parsed = new Date(trimmed);
    if (isNaN(parsed.getTime())) return "";
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function sortHistoryAsc(history: DollarQuote[]): DollarQuote[] {
    return [...history].sort((a, b) => String(a.fecha).localeCompare(String(b.fecha)));
}

function sanitizeHistory(history: DollarQuote[]): DollarQuote[] {
    return sortHistoryAsc(
        history.filter((q) => Number.isFinite(Number(q.venta)) && Number(q.venta) > 0),
    );
}

/**
 * Returns the bolsa `venta` on or before `dateKey` (YYYY-MM-DD), or undefined if none.
 */
export function lookupUsdArsOnOrBefore(history: DollarQuote[], dateKey: string): number | undefined {
    if (!dateKey || history.length === 0) return undefined;
    let best: DollarQuote | undefined;
    for (const quote of history) {
        const fecha = String(quote.fecha).slice(0, 10);
        if (fecha > dateKey) break;
        best = quote;
    }
    return best != null ? Number(best.venta) : undefined;
}

export function conversionRatesForDate(
    displayCurrency: string,
    history: DollarQuote[],
    date: string | Date | undefined,
    fallbackUsdArs?: number,
): Record<string, number> | null {
    if (!date) return null;
    const dateKey = normalizeDateKey(date);
    if (!dateKey) return null;
    const usdArs = lookupUsdArsOnOrBefore(history, dateKey) ?? fallbackUsdArs;
    if (usdArs == null || !Number.isFinite(usdArs) || usdArs <= 0) return null;
    return autoConversionRatesForDisplay(displayCurrency, usdArs);
}

export async function loadBolsaHistory(): Promise<DollarQuote[]> {
    if (memoryHistory) return memoryHistory;
    if (memoryLoadPromise) return memoryLoadPromise;

    memoryLoadPromise = (async () => {
        const cached = responseCache.get<DollarQuote[]>(BOLSA_HISTORY_ENDPOINT);
        if (cached?.length) {
            memoryHistory = sanitizeHistory(cached);
            return memoryHistory;
        }
        const fetched = sanitizeHistory(await fetchHistoricalRates(REFERENCE_DOLLAR_TYPE));
        memoryHistory = fetched;
        if (fetched.length > 0) {
            responseCache.set(BOLSA_HISTORY_ENDPOINT, fetched);
        }
        return fetched;
    })();

    try {
        return await memoryLoadPromise;
    } finally {
        memoryLoadPromise = null;
    }
}

export function getCachedBolsaHistory(): DollarQuote[] {
    if (memoryHistory) return memoryHistory;
    const cached = responseCache.get<DollarQuote[]>(BOLSA_HISTORY_ENDPOINT);
    if (cached?.length) {
        memoryHistory = sanitizeHistory(cached);
        return memoryHistory;
    }
    return [];
}

/** Test helper */
export function resetBolsaHistoryCacheForTests(): void {
    memoryHistory = null;
    memoryLoadPromise = null;
}

export { normalizeDateKey, BOLSA_HISTORY_ENDPOINT };
